/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";
import { SpeedTestOptions } from "@/types/speedtest";
import type { Server } from "@/types/types";
import type { SpeedtestServerQuery } from "@/utils/speedtestSettings";
import {
  parseServerCatalogueResponse,
  type SpeedtestServerCatalogueResult,
} from "./serverCatalogueResponse";

export type { SpeedtestServerCatalogueResult } from "./serverCatalogueResponse";

/** Durable fetch metadata for one Speedtest.net discovery source. */
export interface SpeedtestServerCatalogueStatus {
  stored: boolean;
  updatedAt?: string;
  /** Global regions that the last fetch did not get. Absent when 0. */
  failedRegions?: number;
  /** Global regions that a fetch asks for. Absent for other sources. */
  totalRegions?: number;
}

const addSpeedtestServerQuery = (params: URLSearchParams, query: SpeedtestServerQuery) => {
  if (query.global) {
    params.set("global", "true");
  }
  if (query.latitude !== undefined && query.longitude !== undefined) {
    params.set("latitude", query.latitude.toString());
    params.set("longitude", query.longitude.toString());
  }
};

const requestServers = async (
  testType: string,
  query: SpeedtestServerQuery = {},
  signal?: AbortSignal,
): Promise<SpeedtestServerCatalogueResult> => {
  try {
    const params = new URLSearchParams({ testType });
    addSpeedtestServerQuery(params, query);
    if (query.refresh) {
      params.set("refresh", "true");
    }
    const response = await fetch(getApiUrl(`/servers?${params.toString()}`), { signal });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to fetch servers");
    }
    const data: unknown = await response.json();
    return parseServerCatalogueResponse(data);
  } catch (error) {
    console.error("Error fetching servers:", error);
    throw error;
  }
};

/** Fetches a provider's servers and returns only the server list. */
export async function getServers(testType: string): Promise<Server[]> {
  return (await requestServers(testType)).servers;
}

/** Fetches Speedtest.net servers together with any partial-discovery warnings. */
export async function getSpeedtestServerCatalogue(
  query: SpeedtestServerQuery,
  signal?: AbortSignal,
): Promise<SpeedtestServerCatalogueResult> {
  return requestServers("speedtest", query, signal);
}

/** Reads whether the selected source has been durably fetched without starting discovery. */
export async function getSpeedtestServerCatalogueStatus(
  query: SpeedtestServerQuery,
  signal?: AbortSignal,
): Promise<SpeedtestServerCatalogueStatus> {
  const params = new URLSearchParams();
  addSpeedtestServerQuery(params, query);
  const response = await fetch(
    getApiUrl(`/servers/catalogue/status?${params.toString()}`),
    { signal },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to get server catalogue status");
  }

  const data: unknown = await response.json();
  if (typeof data !== "object" || data === null || !("stored" in data) || typeof data.stored !== "boolean") {
    throw new Error("Invalid server catalogue status response");
  }
  const updatedAt = "updatedAt" in data ? data.updatedAt : undefined;
  const failedRegions = "failedRegions" in data ? data.failedRegions : undefined;
  const totalRegions = "totalRegions" in data ? data.totalRegions : undefined;
  if (
    (updatedAt !== undefined && typeof updatedAt !== "string") ||
    (failedRegions !== undefined && typeof failedRegions !== "number") ||
    (totalRegions !== undefined && typeof totalRegions !== "number")
  ) {
    throw new Error("Invalid server catalogue status response");
  }
  return {
    stored: data.stored,
    updatedAt,
    failedRegions,
    totalRegions,
  };
}

/** Fetches one page of private speed test history for a time range. */
export async function getHistory(
  timeRange: string,
  page: number,
  limit: number
) {
  try {
    const response = await fetch(
      getApiUrl(
        `/speedtest/history?timeRange=${timeRange}&page=${page}&limit=${limit}`
      )
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to fetch history");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching history:", error);
    throw error;
  }
}

/** Fetches the instance's configured speed test schedules. */
export async function getSchedules() {
  try {
    const response = await fetch(getApiUrl("/schedules"));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to fetch schedules");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching schedules:", error);
    throw error;
  }
}

/** Starts a speed test with the selected provider and server options. */
export async function runSpeedTest(options: SpeedTestOptions) {
  try {
    const response = await fetch(getApiUrl("/speedtest"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to run speed test");
    }
    return await response.json();
  } catch (error) {
    console.error("Error running speed test:", error);
    throw error;
  }
}

/** Fetches one page of history exposed by the public dashboard. */
export async function getPublicHistory(
  timeRange: string,
  page: number,
  limit: number
) {
  try {
    const response = await fetch(
      getApiUrl(
        `/speedtest/public/history?timeRange=${timeRange}&page=${page}&limit=${limit}`
      )
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to fetch public history");
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching public history:", error);
    throw error;
  }
}

/** Reads the currently running speed test's status without using a cached response. */
export async function getSpeedTestStatus() {
  try {
    const response = await fetch(getApiUrl("/speedtest/status"), {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to get speed test status");
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting speed test status:", error);
    throw error;
  }
}

/** Starts a traceroute for a host and returns its initial response. */
export async function runTraceroute(host: string) {
  try {
    const response = await fetch(
      getApiUrl(`/traceroute?host=${encodeURIComponent(host)}`)
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to run traceroute");
    }
    return await response.json();
  } catch (error) {
    console.error("Error running traceroute:", error);
    throw error;
  }
}

/** Reads the current traceroute execution status. */
export async function getTracerouteStatus() {
  try {
    const response = await fetch(getApiUrl("/traceroute/status"));
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to get traceroute status");
    }
    return await response.json();
  } catch (error) {
    console.error("Error getting traceroute status:", error);
    throw error;
  }
}
